// The reply's refund amount is the step's amount (2026-10-30,
// docs/decisions/2026-10-30-agent-draft-own-steps.md; docs/contact-view.md
// "The reply follows the steps").
//
// With the agent's draft as the main draft, the steps shown are the agent's
// own, and the reply never does its own refund math: the agent writes the
// template's amount lines (Original Refund / Return Label Deduction / Final
// Refund, set by setRefundAmountLines()) or one refund amount token,
// REFUND_AMOUNT_TOKEN, wherever a refund amount goes ("Refund No Return" and
// any other refund sentence). Code fills it from the shown step's final
// refund, and fills it again whenever the person changes the step (items,
// quantities, reason code, Return / Scrap, who pays). An unfilled token is an
// INSERT placeholder, so Send refuses it (findUnresolvedPlaceholders()).
//
// The Send guard (replyStepWarnings()): a dollar amount in a refund sentence
// that matches no amount on a step shown, and a reply that says "no return"
// while the step is an RMA (or the reverse), are warnings with a one-click
// fix where there is one. They never block. Pure: shared by the server (the
// draft at generation) and the panel (live as the person edits).

export const REFUND_AMOUNT_TOKEN = 'INSERTREFUNDAMOUNT'

// The warning codes (logged with ids only, never the reply's text).
export const REPLY_STEP_WARNING_CODES = Object.freeze({
  AMOUNT_NOT_ON_STEP: 'amount_not_on_step',
  NO_RETURN_BUT_RMA: 'no_return_but_rma',
  RETURN_BUT_SCRAP: 'return_but_scrap',
  // The reply names items the step's items are not (replyItemsWarning()).
  ITEMS_NOT_ON_STEP: 'items_not_on_step',
})

const round2 = (n) => Math.round(Number(n) * 100) / 100
const money = (v) => {
  if (v == null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? round2(n) : null
}
const fmt = (n) => Number(n).toFixed(2)

// The refund steps a reply can talk about, from proposed steps: one entry per
// Process return/refund step with a stored check or a check result (its
// disposition and breakdown), per eBay refund step with a preset amount, and
// per freight / tax refund with an amount. { index, kind, disposition,
// original, deduction, final }. Unchecked indexes are left out.
const EBAY_REFUND_TYPES = new Set(['create_ebay_refund', 'issue_ebay_return_refund', 'inr_issue_refund', 'offer_partial_refund_ebay'])
export function refundsFromSteps(steps = [], { unchecked = [] } = {}) {
  const off = new Set((unchecked ?? []).map(Number))
  const out = []
  ;(steps ?? []).forEach((a, index) => {
    if (!a || off.has(index)) return
    if (a.type === 'process_return_refund') {
      const check = a.params?.dispositionCheck ?? null
      const refund = check?.refund ?? a.params?.checkResult?.refund ?? null
      out.push({
        index, kind: 'return',
        disposition: check?.disposition ?? null,
        original: money(refund?.original), deduction: money(refund?.deduction), final: money(refund?.final),
      })
      return
    }
    if (EBAY_REFUND_TYPES.has(a.type)) {
      const preset = a.params?.presetRefund ?? null
      const amount = preset?.refundType === 'partial' ? money(preset.amount) : money(a.params?.orderTotal)
      out.push({ index, kind: 'refund', disposition: null, original: amount, deduction: null, final: amount })
      return
    }
    if (a.type === 'refund_freight_trq' || a.type === 'refund_tax_trq') {
      const amount = money(a.params?.amount)
      out.push({ index, kind: 'refund_charge', disposition: null, original: amount, deduction: null, final: amount })
    }
  })
  return out
}

// The token filled with the amount ("$12.34"; the template's own "$" kept,
// never doubled). Unchanged when the amount is not known.
export function fillRefundAmountToken(text, amount) {
  const v = money(amount)
  const s = String(text ?? '')
  if (v == null) return s
  return s.replace(new RegExp(String.raw`\$?${REFUND_AMOUNT_TOKEN}(?![A-Z0-9])`, 'g'), `$${fmt(v)}`)
}

// The amount filled earlier swapped for the step's new one: every "$<prev>"
// becomes "$<next>", and a token still there is filled. Unchanged when next
// is not known or equals prev.
export function replaceRefundAmount(text, previous, next) {
  const n = money(next)
  let out = String(text ?? '')
  if (n == null) return out
  const p = money(previous)
  if (p != null && p !== n) out = out.split(`$${fmt(p)}`).join(`$${fmt(n)}`)
  return fillRefundAmountToken(out, n)
}

// The dollar amounts written in the text, per sentence of its visible
// text: { value, raw, sentence }.
const AMOUNT_RE = /\$\s?(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d{1,2}))?(?!\d)/g
const amountValue = (m) => round2(Number(`${m[1].replace(/,/g, '')}.${m[2] ?? '0'}`))
const visible = (t) => String(t ?? '').replace(/<br\s*\/?>|<\/(?:p|li|div|h[1-6]|tr)>/gi, '\n').replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').replace(/&amp;/gi, '&')
const sentencesOf = (t) => visible(t).split(/(?<=[.!?])\s+|\n+/).filter((x) => x.trim())
export function dollarAmountsIn(text) {
  const out = []
  for (const sentence of sentencesOf(text)) {
    for (const m of sentence.matchAll(AMOUNT_RE)) out.push({ value: amountValue(m), raw: m[0], sentence })
  }
  return out
}

// A sentence about a refund (where the step's amount is the only right one).
const REFUND_WORDS_RE = /\b(?:refund(?:ed|ing|s)?|credit(?:ed)?|reimburs\w*|money back)\b/i
// "You do not need to return it" and its kin; and the words of return
// instructions (a label, shipping it back, the RMA number).
const NO_RETURN_RE = /\b(?:(?:do(?:es)?\s*n[o']?t|don['’]t|doesn['’]t|won['’]t|will not)\s+(?:need|have)\s+to\s+(?:return|send|ship|mail)|no\s+(?:need|requirement)\s+to\s+(?:return|send|ship|mail)|no\s+return\s+(?:is\s+)?(?:needed|necessary|required)|keep\s+(?:the|your|it|them)\b|(?:is|are)\s+not\s+(?:needed|required)\s+(?:back|to\s+be\s+returned))/i
const RETURN_INSTRUCTION_RE = /\b(?:return\s+label|prepaid\s+(?:ups|usps|fedex|label)|ship\s+(?:it|them|the\s+\w+(?:\s+\w+)?)\s+back|send\s+(?:it|them|the\s+\w+(?:\s+\w+)?)\s+back|(?:mail|drop)\s+(?:it|them)\s+off|RMA\s*#|once\s+(?:we|our\s+warehouse)\s+receive)/i

// Whether the reply tells the customer they need not return the part, or
// gives them return instructions. Pure.
export function replySaysNoReturn(text) {
  return NO_RETURN_RE.test(visible(text))
}
export function replyGivesReturnInstructions(text) {
  // A sentence that says no return is needed is never an instruction.
  return sentencesOf(text).some((sentence) => !NO_RETURN_RE.test(sentence) && RETURN_INSTRUCTION_RE.test(sentence))
}

// The Send guard's warnings for a reply and the refund steps shown
// (refundsFromSteps(), or the panel's live version with the person's edits).
// [{ code, index, amount?, stepAmount?, message }]. Never blocks.
export function replyStepWarnings({ reply = '', refunds = [] } = {}) {
  const text = String(reply ?? '')
  const list = (refunds ?? []).filter(Boolean)
  const warnings = []
  if (!text.trim() || !list.length) return warnings
  const known = new Set(list.flatMap((r) => [r.original, r.deduction, r.final]).filter((v) => v != null).map((v) => fmt(v)))
  const finals = list.filter((r) => r.final != null)
  const single = finals.length === 1 ? finals[0] : null
  const seen = new Set()
  for (const a of dollarAmountsIn(text)) {
    if (!REFUND_WORDS_RE.test(a.sentence)) continue
    if (known.has(fmt(a.value)) || seen.has(fmt(a.value))) continue
    seen.add(fmt(a.value))
    const stepAmount = single ? single.final : null
    warnings.push({
      code: REPLY_STEP_WARNING_CODES.AMOUNT_NOT_ON_STEP,
      index: single?.index ?? null,
      amount: a.value,
      stepAmount,
      message: stepAmount != null
        ? `The reply says $${fmt(a.value)}, but the step's refund is $${fmt(stepAmount)}.`
        : `The reply says $${fmt(a.value)}, which is not an amount on any step.`,
    })
  }
  const returns = list.filter((r) => r.kind === 'return' && r.disposition)
  if (returns.length) {
    const rma = returns.find((r) => r.disposition === 'return')
    const allScrap = returns.every((r) => r.disposition === 'scrap')
    if (rma && replySaysNoReturn(text)) {
      warnings.push({ code: REPLY_STEP_WARNING_CODES.NO_RETURN_BUT_RMA, index: rma.index, message: 'The reply says the customer does not need to return the part, but the step is a return (RMA): change the reply or the step to Scrap.' })
    } else if (allScrap && replyGivesReturnInstructions(text)) {
      warnings.push({ code: REPLY_STEP_WARNING_CODES.RETURN_BUT_SCRAP, index: returns[0].index, message: 'The reply gives return instructions, but the step is Scrap (no return): change the reply or the step to Return.' })
    }
  }
  return warnings
}

// "Use the step's amount": every "$<amount>" swapped for the step's.
export function withStepAmount(text, amount, stepAmount) {
  const a = money(amount)
  const b = money(stepAmount)
  const s = String(text ?? '')
  if (a == null || b == null || a === b) return s
  return s.replace(AMOUNT_RE, (raw, ...rest) => (amountValue([raw, ...rest]) === a ? `$${fmt(b)}` : raw))
}

// The decision's record of the Send guard's warnings the person sent past
// (2026-10-30): codes and step indexes only (never the reply's text or an
// amount). warnings: what the panel sent, [{ code, index }]; anything else
// is dropped. null when there is none.
export function replyStepWarningsEntry({ warnings = [], actions = [], status = null, actedBy = null, at = new Date().toISOString() } = {}) {
  const codes = new Set(Object.values(REPLY_STEP_WARNING_CODES))
  const list = (Array.isArray(warnings) ? warnings : [])
    .filter((w) => w && codes.has(w.code))
    .map((w) => ({ code: w.code, index: Number.isInteger(w.index) && w.index >= 0 && w.index < (actions ?? []).length ? w.index : null }))
    .slice(0, 10)
  if (!list.length) return null
  return {
    service: 'CXHub', method: 'DECISION', phase: 'approve', at, actedBy, endpoint: null, headers: null,
    label: `Decided (${status ?? 'unknown'}) with reply warnings: ${list.map((w) => `${w.code}${w.index != null ? ` (step ${w.index + 1})` : ''}`).join(', ')}`,
    replyStepWarnings: list,
  }
}

// --- The items the reply names (2026-10-30, Jenn) ------------------------------
// A best-effort check before Send: the part words and positions the reply
// names ("the rear pad set and rotors") against the shown step's items. A
// word counts only when the order has it on a line the step does NOT select
// (so "rear" is flagged only when the step's items are not rear ones but the
// order has rear ones): the reply names something on the order the step
// doesn't do. Never blocks; the warning names the step's items.
export const ITEMS_WARNING_CODE = 'items_not_on_step'
const POSITION_WORDS = new Set(['front', 'rear', 'left', 'right', 'upper', 'lower', 'inner', 'outer', 'driver', 'passenger'])
const GENERIC_WORDS = new Set(['set', 'kit', 'pair', 'pack', 'piece', 'pieces', 'assembly', 'complete', 'new', 'oem', 'replacement', 'part', 'item', 'each', 'side', 'both', 'and', 'with', 'for', 'the', 'includes', 'include', 'inch', 'style', 'type', 'fits', 'select', 'models', 'model'])
const singular = (w) => (w.length > 3 && /s$/.test(w) && !/ss$/.test(w) ? w.replace(/(?:ies)$/, 'y').replace(/(?<!y)s$/, '') : w)
const wordsOf = (t) => new Set(String(t ?? '').toLowerCase().replace(/&/g, ' and ').split(/[^a-z]+/).filter((w) => w.length >= 3 || POSITION_WORDS.has(w)).map(singular))
const skuKey = (i) => String(i?.sku ?? '').trim().toUpperCase()

// The one step with an item selection the reply's items are judged against:
// a return, replacement or cancellation. null when there is none or several.
export function itemStepOf(steps = [], { unchecked = [] } = {}) {
  const off = new Set((unchecked ?? []).map(Number))
  const list = (steps ?? []).map((a, index) => ({ a, index })).filter(({ a, index }) => !off.has(index) && ['process_return_refund', 'process_replacement', 'cancel_order_trq'].includes(a?.type) && Array.isArray(a?.itemSelection?.allItems) && a.itemSelection.allItems.length && !a?.params?.overdueRefund)
  return list.length === 1 ? list[0] : null
}

// { words: [what the reply names that the step's items don't], items: [the
// step's item names] } or null. selected: the step's items now ({ sku,
// quantity }); allItems: the order's lines with their descriptions.
export function replyItemsMismatch({ reply = '', selected = [], allItems = [] } = {}) {
  const text = visible(reply)
  if (!text.trim() || !Array.isArray(selected) || !selected.length) return null
  const picked = new Set(selected.map(skuKey))
  const lines = (allItems ?? []).filter((l) => l?.sku && String(l.description ?? '').trim())
  const mine = lines.filter((l) => picked.has(skuKey(l)))
  if (!mine.length) return null
  const stepWords = new Set(mine.flatMap((l) => [...wordsOf(l.description)]))
  const otherWords = new Set(lines.filter((l) => !picked.has(skuKey(l))).flatMap((l) => [...wordsOf(l.description)]))
  const replyWords = wordsOf(text)
  const words = [...replyWords].filter((w) => !GENERIC_WORDS.has(w) && otherWords.has(w) && !stepWords.has(w))
  if (!words.length) return null
  const items = mine.map((l) => {
    const q = Number(selected.find((s) => skuKey(s) === skuKey(l))?.quantity) || 1
    return `${String(l.description).trim()}${q > 1 ? ` (x${q})` : ''}`
  })
  return { words, items }
}

// The warning for replyStepWarnings()'s list: { code, index, message } or null.
export function replyItemsWarning({ reply = '', index = null, selected = [], allItems = [] } = {}) {
  const m = replyItemsMismatch({ reply, selected, allItems })
  if (!m) return null
  return {
    code: ITEMS_WARNING_CODE,
    index,
    message: `The reply names ${m.words.map((w) => `"${w}"`).join(', ')}, which the step's items are not. The step's items: ${m.items.join(', ')}. Check the item names in the reply.`,
  }
}
